import EventEmitter from "events";
import { v4 as uuidV4 } from 'uuid';
import { DateTime } from "luxon";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import axiosRetry from 'axios-retry';
import PQueue, { QueueAddOptions } from 'p-queue';
import PriorityQueue from "p-queue/dist/priority-queue";

import { DEFAULT_DATABASE_NAME, DEFAULT_REFETCH_AGE_DAYS, DEFAULT_SEED_STRATEGY_DAYS, DEFAULT_TILE_OBJECT_STORE_NAME, DEFAULT_UI_TITLE_OP_INPROGRESS, DEFAULT_OPERATION_OBJECT_STORE_NAME, DEFAULT_OPERATION_EVENT_CHANNEL_NAME, DEFAULT_OPERATION_HISTORY_MAX_AGE_DAYS } from "../../../ServiceWorker/TileCache/indexedDb/constants";
import { ITileCacheSeeder, ITileCacheSeedOperation, TileCacheSeedOperationChangeEvent, TileCacheSeedOperationMessageType, TileCacheSeedOperationStartedEvent, TileCacheSeedOperationStatus } from "./types";
import { getTilesWithinBounds } from "../geoUtils";
import { IIndexedDbTileCacheOptions } from "./indexedDB/types";
import { Logic } from "./indexedDB/logic";
import { ITileCoordinates } from "../tileCoordinates";


/**
 * Class for a bulk seeder for use with IndexedDbTileCache
 */
export class AjaxTileCacheSeeder extends EventEmitter implements ITileCacheSeeder {

    private fetches: { opId: string, queue: PQueue<PriorityQueue, QueueAddOptions>, cumlTotalBytes: number, cumlTotal: number, error?: Error | null, isCancelled: boolean }[] = [];

    constructor(
        private options: IIndexedDbTileCacheOptions = {}
    ) {
        super();

        this.options.databaseName = this.options.databaseName || DEFAULT_DATABASE_NAME;
        this.options.tileRefetchAgeDays = this.options.tileRefetchAgeDays || DEFAULT_REFETCH_AGE_DAYS;
        this.options.seedStrategy = this.options.seedStrategy || DEFAULT_SEED_STRATEGY_DAYS;
        this.options.operationObjectStoreName = this.options.operationObjectStoreName || DEFAULT_OPERATION_OBJECT_STORE_NAME;
        this.options.tileObjectStoreName = this.options.tileObjectStoreName || DEFAULT_TILE_OBJECT_STORE_NAME;
        this.options.eventBroadcastChannelName = this.options.eventBroadcastChannelName || DEFAULT_OPERATION_EVENT_CHANNEL_NAME;
        this.options.operationHistoryMaxAgeDays = this.options.operationHistoryMaxAgeDays || DEFAULT_OPERATION_HISTORY_MAX_AGE_DAYS;
        this.options.uiTitleOpInProgress = this.options.uiTitleOpInProgress ||  DEFAULT_UI_TITLE_OP_INPROGRESS;

        const broadcastChannel = new BroadcastChannel(this.options.eventBroadcastChannelName);
        broadcastChannel.onmessage = (event) => {
            const data = event.data as TileCacheSeedOperationChangeEvent;
            this.emitChangeEvent(data);
        };
    }

    public readonly id: string = "AjaxTileCacheSeeder";
    public readonly name: string = "Default";

    public isSupported = (): boolean => true;

    public maxInProgressDownloads = (): number => 3;

    public getSeedHistory = (): Promise<ITileCacheSeedOperation[]> => {
        return Logic.getOps(this.options);
    }

    public cullSeedHistory = (currentDate: DateTime): Promise<void> => {
        return Logic.cullOps(this.options, this.id, currentDate, (op) => this.fetches.findIndex(f => f.opId === op.id) === -1);
    }

    /**
     * Seeds an area of tiles by the given bounding box, the maximal z value and the optional minimal z value.
     *
     * Returns an opertion handle with a unique identifier for the batch operation.
     */
    public seedFromBounds = async (crs: L.CRS, boundingBox: L.LatLngBounds, maxZoom: number, minZoom: number = 0, urlTemplate: string, urlSubdomains?: string[] | null, thumbUrl?: string | null): Promise<ITileCacheSeedOperation> => {

        const numInProgress = await Logic.getNumInProgressOps(this.options);
        if (numInProgress >= this.maxInProgressDownloads())
            throw new Error("Maximum number of in-progress downloads reached");

        const tileCoords = getTilesWithinBounds(crs, boundingBox, maxZoom, minZoom);
        const tileCoordsToFetch = await Logic.filterCachedTileCoords(this.options, tileCoords, urlTemplate);
        const requestUrls = tileCoordsToFetch.map(c => { return { internalUrl: Logic.createInternalTileUrl(urlTemplate, c), requestUrl: Logic.createTileUrl(urlTemplate, urlSubdomains ?? [], c)} });

        const op: ITileCacheSeedOperation = {
            id: Logic.BGFETCH_ID_PREFIX + uuidV4(),
            seederId: this.id,
            status: TileCacheSeedOperationStatus.InProgress,
            startedAt: Date.now(),
            numTotal: tileCoordsToFetch.length,
            downloadedTotal: null,
            downloadedBytes: null,
            thumbUrl: thumbUrl ?? null,
            requestUrls
        };

        await Logic.createOp(this.options, op);

        this.emitStartedEvent({ type: TileCacheSeedOperationMessageType.starting, op });

        if (op.numTotal === 0) {
            await Logic.updateOpStatus(this.options, op.id, TileCacheSeedOperationStatus.Succeeded, null, 0)
                .then((result) => {
                    if (result.didChange)
                        this.emit(TileCacheSeedOperationMessageType.statusChange, { type: TileCacheSeedOperationMessageType.statusChange, opId: op.id, status: TileCacheSeedOperationStatus.Succeeded });
                });
            
            return Promise.resolve(op);
        }

        const queue = this.buildQueue(op.id, tileCoordsToFetch, urlTemplate, urlSubdomains ?? []);

        this.fetches.push({ opId: op.id, queue, cumlTotalBytes: 0, cumlTotal: 0, isCancelled: false });
        queue.start();

        this.emit(TileCacheSeedOperationMessageType.statusChange, { type: TileCacheSeedOperationMessageType.statusChange, opId: op.id, status: TileCacheSeedOperationStatus.InProgress });

        return op;
    }

    private buildQueue(opId: string, tileCoords: ITileCoordinates[], urlTemplate: string, urlSubdomains: string[]) {

        const client = axios.create();
        axiosRetry(client, { retries: 20, retryDelay: (retryNumber, error) => axiosRetry.exponentialDelay(retryNumber, error, 1000) });

        const queue = new PQueue({ concurrency: 1, timeout: 10000, throwOnTimeout: true });

        tileCoords.forEach(async (c) => {
            await queue.add(() => this.buildTileRequest(opId, client, c, urlTemplate, urlSubdomains));
        });

        queue.on('error', async (requestError: AxiosError) => { 

            queue.clear();

            const error = new Error(requestError.message);

            const fetch = this.fetches.find(f => f.opId === opId);
            if (fetch)
                fetch.error = error;

            await Logic.updateOpStatus(this.options, opId, TileCacheSeedOperationStatus.Failed, error)
                .then((result) => {
                    if (result.didChange)
                        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.statusChange, opId, status: TileCacheSeedOperationStatus.Failed, error })
                });
        });

        queue.on('idle', async () => {

            const fetch = this.fetches.find(f => f.opId === opId);

            if (fetch && !fetch.error) {

                const newStatus = fetch.isCancelled === true ? TileCacheSeedOperationStatus.Cancelled : TileCacheSeedOperationStatus.Succeeded;
                await Logic.updateOpStatus(this.options, opId, newStatus)
                    .then((result) => {
                        if (result.didChange)
                            this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.statusChange, opId, status: newStatus })
                    });
            }

            if (fetch)
                fetch.queue.removeAllListeners();

            this.fetches = this.fetches.filter(f => f.opId !== opId);   //Remove fetch

        });

        return queue;

    }

    private buildTileRequest = (opId: string, client: AxiosInstance, tileCoords: ITileCoordinates, urlTemplate: string, urlSubdomains: string[]): Promise<AxiosResponse<any, any>> => {

        const request = client.get(Logic.createTileUrl(urlTemplate, urlSubdomains, tileCoords), { responseType: 'arraybuffer' });

        request.then(async (r) => {
            const fetch = this.fetches.find(f => f.opId === opId);

            if (!fetch)
                throw new Error('Fetch not found');

            const contentType =  r.headers["content-type"] as string;
            const contentLength = r.headers["content-length"] ? +r.headers["content-length"] : 0;

            fetch.cumlTotalBytes += contentLength;
            const downloadedBytes = fetch!.cumlTotalBytes;

            fetch.cumlTotal++;
            const downloadedTotal = fetch!.cumlTotal;

            await Logic.cacheTileFetchResult(this.options, contentType, r.data, Logic.createInternalTileUrl(urlTemplate, tileCoords));
           
            await Logic.updateOpStatus(this.options, opId, TileCacheSeedOperationStatus.InProgress, null, downloadedBytes, downloadedTotal)
                .then((result) => {
                    if (result.didChange)
                        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.progressChange, opId: opId, downloadedBytes, downloadedTotal });
                });
            
        });

        return request;
    }

    public canRemove = (op: ITileCacheSeedOperation): boolean => op.status === TileCacheSeedOperationStatus.Failed || op.status === TileCacheSeedOperationStatus.Cancelled || op.status === TileCacheSeedOperationStatus.Succeeded;
    public removeOperation = async (opId: string): Promise<ITileCacheSeedOperation> => {

        const op = await Logic.getOp(this.options, opId);
        if (!op)
            throw new Error("Operation not found");

        if (!this.canRemove(op))
            throw new Error("Operation is not in a suitable state");

        await Logic.deleteOp(this.options, opId);
        this.fetches = this.fetches.filter(f => f.opId !== opId);   //Remove fetch

        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.removed, opId: op.id });

        return op;
    }

    public canCancel = (op: ITileCacheSeedOperation): boolean => op.status === TileCacheSeedOperationStatus.InProgress;
    public cancelOperation = async (opId: string): Promise<ITileCacheSeedOperation> => {

        const op = await Logic.getOp(this.options, opId);
        if (!op)
            throw new Error("Operation not found");

        if (!this.canCancel(op))
            throw new Error("Operation is not in a suitable state");

        const fetch = this.fetches.find(f => f.opId === opId);

        if (fetch) {
            fetch.isCancelled = true;
            fetch.queue.clear();
        }
        else {
            //No fetch found, so - assume already aborted & fire change event
            await Logic.updateOpStatus(this.options, opId, TileCacheSeedOperationStatus.Cancelled, null, null)
                .then((result) => {
                    if (result.didChange)
                        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.statusChange, opId, status: TileCacheSeedOperationStatus.Cancelled });
                });
        }

        return op;
    }

    //Internals
    private emitStartedEvent = (event: TileCacheSeedOperationStartedEvent) => {
        this.emit(event.type, event);
    }

    private emitChangeEvent = (event: TileCacheSeedOperationChangeEvent) => {
        this.emit(event.type, event);
    }

}